import React, { useCallback } from 'react';
import { adminAPI } from '../../services/api';
import AsyncTypeahead, { AsyncTypeaheadOption } from '../ui/AsyncTypeahead';

export type ReservationTypeaheadOption = AsyncTypeaheadOption & {
  reservationRef?: string;
  startDate?: string;
  endDate?: string;
  customerName?: string | null;
};

type ReservationTypeaheadProps = {
  value: ReservationTypeaheadOption | null;
  onChange: (value: ReservationTypeaheadOption | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

function formatDateRange(start?: string, end?: string): string {
  if (!start && !end) return '';
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return `from ${fmt(start)}`;
  return `until ${fmt(end!)}`;
}

export default function ReservationTypeahead({
  value,
  onChange,
  placeholder = 'Search reservations by ref or customer...',
  className,
  disabled,
}: ReservationTypeaheadProps) {
  const loadReservations = useCallback(
    async (query: string): Promise<ReservationTypeaheadOption[]> => {
      const response = await adminAPI.getAllReservations({ limit: 50, page: 1 });

      const payload = response?.data;
      const records: any[] = Array.isArray(payload?.reservations)
        ? payload.reservations
        : Array.isArray(payload)
        ? payload
        : [];

      const lq = query.trim().toLowerCase();

      return records
        .filter((r: any) => {
          if (!r?.id) return false;
          if (!lq) return true;
          const ref = (r.reservationRef || r.reservation_ref || '').toLowerCase();
          const custRef = (r.customerReference || r.customer_reference || '').toLowerCase();
          const custName = (
            r.tripDetails?.customer_name ||
            r.customer?.name ||
            r.customerName ||
            ''
          ).toLowerCase();
          return ref.includes(lq) || custRef.includes(lq) || custName.includes(lq);
        })
        .map((r: any) => {
          const ref = r.reservationRef || r.reservation_ref;
          const label = ref || `Reservation ${String(r.id).slice(0, 8)}`;
          const custName =
            r.tripDetails?.customer_name ||
            r.customer?.name ||
            r.customerReference ||
            null;
          const dateRange = formatDateRange(r.startDate || r.start_date, r.endDate || r.end_date);
          const descParts = [dateRange, custName].filter(Boolean);
          return {
            id: String(r.id),
            label,
            description: descParts.length > 0 ? descParts.join(' · ') : undefined,
            reservationRef: ref,
            startDate: r.startDate || r.start_date,
            endDate: r.endDate || r.end_date,
            customerName: custName,
          } satisfies ReservationTypeaheadOption;
        });
    },
    [],
  );

  return (
    <AsyncTypeahead
      value={value}
      onChange={(option) => onChange(option as ReservationTypeaheadOption | null)}
      loadOptions={loadReservations}
      placeholder={placeholder}
      emptyMessage="No reservations matched your search."
      className={className}
      disabled={disabled}
    />
  );
}
